// src/components/ui/Button.tsx
import { cva } from "class-variance-authority";

// src/utils/cn.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/ui/Button.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var buttonVariants = cva(
  // Base styles applied to all buttons
  "inline-flex items-center justify-center text-center font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2",
  {
    variants: {
      variant: {
        solid: "",
        outline: "bg-transparent border-2",
        ghost: "bg-transparent hover:bg-opacity-10",
        link: "bg-transparent underline-offset-4 hover:underline"
      },
      color: {
        martinique: "",
        dark: "",
        orange: "",
        gray: "",
        success: "",
        warning: "",
        error: "",
        info: ""
      },
      size: {
        xs: "text-xs px-3 py-1.5 rounded",
        sm: "text-sm px-4 py-2 rounded-md",
        md: "text-base px-6 py-3 rounded-md",
        lg: "text-lg px-8 py-4 rounded-lg",
        xl: "text-xl px-10 py-5 rounded-lg"
      },
      fullWidth: {
        true: "w-full",
        false: ""
      },
      rounded: {
        none: "rounded-none",
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        full: "rounded-full"
      }
    },
    compoundVariants: [
      // Martinique (Primary Brand) Solid
      {
        variant: "solid",
        color: "martinique",
        className: "bg-brand-martinique text-white border-brand-martinique hover:bg-opacity-90 focus:ring-brand-martinique active:bg-opacity-80"
      },
      // Martinique Outline
      {
        variant: "outline",
        color: "martinique",
        className: "text-brand-martinique border-brand-martinique hover:bg-brand-martinique hover:text-white focus:ring-brand-martinique"
      },
      // Martinique Ghost
      {
        variant: "ghost",
        color: "martinique",
        className: "text-brand-martinique hover:bg-brand-martinique focus:ring-brand-martinique"
      },
      // Dark Solid
      {
        variant: "solid",
        color: "dark",
        className: "bg-brand-dark text-white border-brand-dark hover:bg-opacity-90 focus:ring-brand-dark active:bg-opacity-80"
      },
      // Dark Outline
      {
        variant: "outline",
        color: "dark",
        className: "text-brand-dark border-brand-dark hover:bg-brand-dark hover:text-white focus:ring-brand-dark"
      },
      // Orange (Accent) Solid
      {
        variant: "solid",
        color: "orange",
        className: "bg-brand-orange text-white border-brand-orange hover:bg-opacity-90 focus:ring-brand-orange active:bg-opacity-80"
      },
      // Orange Outline
      {
        variant: "outline",
        color: "orange",
        className: "text-brand-orange border-brand-orange hover:bg-brand-orange hover:text-white focus:ring-brand-orange"
      },
      // Orange Ghost
      {
        variant: "ghost",
        color: "orange",
        className: "text-brand-orange hover:bg-brand-orange focus:ring-brand-orange"
      },
      // Gray Solid
      {
        variant: "solid",
        color: "gray",
        className: "bg-brand-gray text-white border-brand-gray hover:bg-opacity-90 focus:ring-brand-gray active:bg-opacity-80"
      },
      // Gray Outline
      {
        variant: "outline",
        color: "gray",
        className: "text-brand-gray border-brand-gray hover:bg-brand-gray hover:text-white focus:ring-brand-gray"
      },
      // Success Solid
      {
        variant: "solid",
        color: "success",
        className: "bg-brand-success text-white border-brand-success hover:bg-opacity-90 focus:ring-brand-success active:bg-opacity-80"
      },
      // Success Outline
      {
        variant: "outline",
        color: "success",
        className: "text-brand-success border-brand-success hover:bg-brand-success hover:text-white focus:ring-brand-success"
      },
      // Warning Solid
      {
        variant: "solid",
        color: "warning",
        className: "bg-brand-warning text-white border-brand-warning hover:bg-opacity-90 focus:ring-brand-warning active:bg-opacity-80"
      },
      // Warning Outline
      {
        variant: "outline",
        color: "warning",
        className: "text-brand-warning border-brand-warning hover:bg-brand-warning hover:text-white focus:ring-brand-warning"
      },
      // Error Solid
      {
        variant: "solid",
        color: "error",
        className: "bg-brand-error text-white border-brand-error hover:bg-opacity-90 focus:ring-brand-error active:bg-opacity-80"
      },
      // Error Outline
      {
        variant: "outline",
        color: "error",
        className: "text-brand-error border-brand-error hover:bg-brand-error hover:text-white focus:ring-brand-error"
      },
      // Info Solid
      {
        variant: "solid",
        color: "info",
        className: "bg-brand-info text-white border-brand-info hover:bg-opacity-90 focus:ring-brand-info active:bg-opacity-80"
      },
      // Info Outline
      {
        variant: "outline",
        color: "info",
        className: "text-brand-info border-brand-info hover:bg-brand-info hover:text-white focus:ring-brand-info"
      }
    ],
    defaultVariants: {
      variant: "solid",
      color: "martinique",
      size: "md",
      fullWidth: false,
      rounded: "md"
    }
  }
);
var Button = ({
  children,
  className,
  variant,
  color,
  size,
  fullWidth,
  rounded,
  isLoading = false,
  leftIcon,
  rightIcon,
  disabled,
  ...props
}) => {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      className: cn(
        buttonVariants({ variant, color, size, fullWidth, rounded }),
        className
      ),
      disabled: disabled || isLoading,
      ...props,
      children: [
        isLoading && /* @__PURE__ */ jsxs(
          "svg",
          {
            className: "animate-spin -ml-1 mr-3 h-5 w-5",
            xmlns: "http://www.w3.org/2000/svg",
            fill: "none",
            viewBox: "0 0 24 24",
            children: [
              /* @__PURE__ */ jsx(
                "circle",
                {
                  className: "opacity-25",
                  cx: "12",
                  cy: "12",
                  r: "10",
                  stroke: "currentColor",
                  strokeWidth: "4"
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  className: "opacity-75",
                  fill: "currentColor",
                  d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                }
              )
            ]
          }
        ),
        !isLoading && leftIcon && /* @__PURE__ */ jsx("span", { className: "mr-2", children: leftIcon }),
        children,
        !isLoading && rightIcon && /* @__PURE__ */ jsx("span", { className: "ml-2", children: rightIcon })
      ]
    }
  );
};

// src/components/layout/Card.tsx
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var cardVariants = cva2("bg-white rounded-lg transition-all", {
  variants: {
    shadow: {
      none: "",
      soft: "shadow-soft",
      medium: "shadow-medium",
      strong: "shadow-strong"
    },
    padding: {
      none: "p-0",
      sm: "p-4",
      md: "p-6",
      lg: "p-8",
      xl: "p-10"
    },
    hover: {
      none: "",
      lift: "hover:shadow-strong hover:-translate-y-1",
      glow: "hover:shadow-medium hover:border-brand-orange"
    }
  },
  defaultVariants: {
    shadow: "soft",
    padding: "md",
    hover: "none"
  }
});
var Card = ({
  children,
  className,
  header,
  footer,
  shadow,
  padding,
  hover
}) => {
  return /* @__PURE__ */ jsxs2("div", { className: cn(cardVariants({ shadow, padding, hover }), className), children: [
    header && /* @__PURE__ */ jsx2("div", { className: "border-b border-gray-200 pb-4 mb-4", children: header }),
    children,
    footer && /* @__PURE__ */ jsx2("div", { className: "border-t border-gray-200 pt-4 mt-4", children: footer })
  ] });
};
var StatCard = ({
  label,
  value,
  icon,
  trend,
  className
}) => {
  return /* @__PURE__ */ jsx2(Card, { shadow: "medium", padding: "lg", className, children: /* @__PURE__ */ jsxs2("div", { className: "flex items-center justify-between", children: [
    /* @__PURE__ */ jsxs2("div", { children: [
      /* @__PURE__ */ jsx2("p", { className: "text-sm text-brand-gray mb-1", children: label }),
      /* @__PURE__ */ jsx2("p", { className: "text-3xl font-bold font-agency text-brand-martinique", children: value }),
      trend && /* @__PURE__ */ jsxs2(
        "div",
        {
          className: cn("flex items-center gap-1 mt-2 text-sm", {
            "text-brand-success": trend.direction === "up",
            "text-brand-error": trend.direction === "down"
          }),
          children: [
            trend.direction === "up" ? /* @__PURE__ */ jsx2(
              "svg",
              {
                className: "w-4 h-4",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx2(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d: "M5 15l7-7 7 7"
                  }
                )
              }
            ) : /* @__PURE__ */ jsx2(
              "svg",
              {
                className: "w-4 h-4",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx2(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d: "M19 9l-7 7-7-7"
                  }
                )
              }
            ),
            /* @__PURE__ */ jsxs2("span", { children: [
              Math.abs(trend.value),
              "%"
            ] })
          ]
        }
      )
    ] }),
    icon && /* @__PURE__ */ jsx2("div", { className: "text-brand-orange opacity-80 text-4xl", children: icon })
  ] }) });
};

// src/components/cards/Card.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var variantStyles = {
  default: "bg-white border border-brand-gray/20",
  outlined: "bg-transparent border-2 border-brand-martinique",
  elevated: "bg-white shadow-lg"
};
var paddingStyles = {
  none: "p-0",
  sm: "p-4",
  md: "p-6",
  lg: "p-8"
};
var Card2 = ({
  children,
  variant = "default",
  padding = "md",
  hoverable = false,
  className = "",
  ...props
}) => {
  const hoverStyles = hoverable ? "transition-all duration-200 hover:shadow-xl hover:-translate-y-1 cursor-pointer" : "";
  return /* @__PURE__ */ jsx3(
    "div",
    {
      className: `rounded-xl ${variantStyles[variant]} ${paddingStyles[padding]} ${hoverStyles} ${className}`,
      ...props,
      children
    }
  );
};
var CardHeader = ({
  title,
  subtitle,
  action,
  className = ""
}) => {
  return /* @__PURE__ */ jsxs3("div", { className: `flex items-start justify-between mb-4 ${className}`, children: [
    /* @__PURE__ */ jsxs3("div", { children: [
      /* @__PURE__ */ jsx3("h3", { className: "text-h4 font-agency font-semibold text-brand-martinique", children: title }),
      subtitle && /* @__PURE__ */ jsx3("p", { className: "mt-1 text-sm-custom text-brand-gray font-martianmono", children: subtitle })
    ] }),
    action && /* @__PURE__ */ jsx3("div", { className: "ml-4", children: action })
  ] });
};
var CardBody = ({
  children,
  className = ""
}) => {
  return /* @__PURE__ */ jsx3("div", { className: `text-brand-gray font-martianmono ${className}`, children });
};
var CardFooter = ({
  children,
  className = ""
}) => {
  return /* @__PURE__ */ jsx3(
    "div",
    {
      className: `mt-6 pt-4 border-t border-brand-gray/20 flex items-center justify-between ${className}`,
      children
    }
  );
};
var FeatureCard = ({
  icon,
  title,
  description,
  action,
  className = ""
}) => {
  return /* @__PURE__ */ jsx3(Card2, { variant: "elevated", hoverable: true, className, children: /* @__PURE__ */ jsxs3("div", { className: "flex flex-col items-center text-center", children: [
    /* @__PURE__ */ jsx3("div", { className: "mb-4 p-4 rounded-full bg-brand-orange/10 text-brand-orange", children: icon }),
    /* @__PURE__ */ jsx3("h3", { className: "text-h4 font-agency font-semibold text-brand-martinique mb-2", children: title }),
    /* @__PURE__ */ jsx3("p", { className: "text-sm-custom text-brand-gray font-martianmono mb-4", children: description }),
    action && /* @__PURE__ */ jsx3("div", { className: "mt-auto", children: action })
  ] }) });
};

// src/components/cards/ContentCard.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var ContentCard = ({
  image,
  imageAlt = "",
  title,
  description,
  buttonText,
  href = "#",
  theme = "light",
  className = "",
  onClick
}) => {
  const themeStyles = {
    dark: {
      card: "bg-surface-950 shadow-lg hover:shadow-2xl border border-surface-800",
      title: "text-white hover:text-democracy-500",
      description: "text-gray-300",
      button: "border-democracy-600 text-democracy-400 hover:border-democracy-500 hover:bg-democracy-500 hover:text-white"
    },
    light: {
      card: "bg-white shadow-md hover:shadow-xl border border-gray-200",
      title: "text-black hover:text-democracy-600",
      description: "text-gray-600",
      button: "border-democracy-600 text-democracy-600 hover:border-democracy-500 hover:bg-democracy-600 hover:text-white"
    }
  };
  const styles = themeStyles[theme];
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      className: `
        overflow-hidden rounded-xl
        ${styles.card}
        transition-all duration-300 ease-in-out
        cursor-pointer
        ${className}
      `,
      onClick,
      children: [
        /* @__PURE__ */ jsxs4("div", { className: "relative overflow-hidden group", children: [
          /* @__PURE__ */ jsx4(
            "img",
            {
              src: image,
              alt: imageAlt || title,
              className: "w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
            }
          ),
          /* @__PURE__ */ jsx4("div", { className: "absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" })
        ] }),
        /* @__PURE__ */ jsxs4("div", { className: "p-8 sm:p-9 md:p-7 xl:p-9", children: [
          /* @__PURE__ */ jsx4("h3", { className: "mb-4", children: /* @__PURE__ */ jsx4(
            "a",
            {
              href,
              className: `
              block text-xl sm:text-2xl md:text-xl lg:text-2xl xl:text-xl 2xl:text-2xl
              font-agency font-bold uppercase
              ${styles.title}
              transition-colors duration-200
              leading-tight
            `,
              onClick: (e) => {
                if (onClick) {
                  e.preventDefault();
                  onClick();
                }
              },
              children: title
            }
          ) }),
          /* @__PURE__ */ jsx4("p", { className: `
          mb-7 text-base leading-relaxed
          font-body
          ${styles.description}
        `, children: description }),
          buttonText && /* @__PURE__ */ jsx4(
            "a",
            {
              href,
              className: `
              inline-block rounded-full
              border-2
              px-7 py-2.5
              text-base font-medium font-body
              transition-all duration-200
              ${styles.button}
            `,
              onClick: (e) => {
                if (onClick) {
                  e.preventDefault();
                  onClick();
                }
              },
              children: buttonText
            }
          )
        ] })
      ]
    }
  );
};
var ContentCardGrid = ({
  children,
  theme = "light",
  columns = { sm: 2, lg: 3 },
  className = ""
}) => {
  const bgStyles = theme === "dark" ? "bg-surface-950" : "bg-surface-50";
  const colStyles = `
    sm:grid-cols-${columns.sm || 2}
    lg:grid-cols-${columns.lg || 3}
  `;
  return /* @__PURE__ */ jsx4("section", { className: `${bgStyles} py-10 lg:py-20 ${className}`, children: /* @__PURE__ */ jsx4("div", { className: "container mx-auto px-4", children: /* @__PURE__ */ jsx4("div", { className: `grid gap-8 ${colStyles}`, children }) }) });
};

// src/components/forms/Input.tsx
import { forwardRef } from "react";
import { cva as cva3 } from "class-variance-authority";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var inputVariants = cva3(
  "w-full bg-transparent rounded-md transition outline-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "px-3 py-2 text-sm",
        md: "px-5 py-2.5 text-base",
        lg: "px-6 py-3 text-lg"
      },
      state: {
        default: "border border-brand-gray/30 text-gray-900 focus:border-brand-martinique focus:ring-2 focus:ring-brand-martinique/20",
        error: "border border-brand-error text-gray-900 focus:border-brand-error focus:ring-2 focus:ring-brand-error/20",
        success: "border border-brand-success text-gray-900 focus:border-brand-success focus:ring-2 focus:ring-brand-success/20",
        disabled: "border border-brand-gray/20 bg-gray-100"
      }
    },
    defaultVariants: {
      size: "md",
      state: "default"
    }
  }
);
var Input = forwardRef(
  ({
    label,
    error,
    hint,
    leftIcon,
    rightIcon,
    leftAddon,
    rightAddon,
    className,
    size,
    state,
    disabled,
    ...props
  }, ref) => {
    const inputState = disabled ? "disabled" : error ? "error" : state || "default";
    return /* @__PURE__ */ jsxs5("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsx5("label", { className: "mb-2 block text-base font-medium text-brand-martinique", children: label }),
      /* @__PURE__ */ jsxs5("div", { className: "relative flex items-center", children: [
        leftAddon && /* @__PURE__ */ jsx5("span", { className: "h-full rounded-l-md border border-r-0 border-brand-gray/30 bg-gray-100 px-4 py-2.5 text-base text-brand-gray", children: leftAddon }),
        leftIcon && !leftAddon && /* @__PURE__ */ jsx5("span", { className: "absolute left-4 text-brand-gray", children: leftIcon }),
        /* @__PURE__ */ jsx5(
          "input",
          {
            ref,
            className: cn(
              inputVariants({ size, state: inputState }),
              {
                "pl-12": leftIcon && !leftAddon,
                "pr-12": rightIcon && !rightAddon,
                "rounded-l-none": leftAddon,
                "rounded-r-none": rightAddon
              },
              className
            ),
            disabled,
            ...props
          }
        ),
        rightIcon && !rightAddon && /* @__PURE__ */ jsx5("span", { className: "absolute right-4 text-brand-gray", children: rightIcon }),
        rightAddon && /* @__PURE__ */ jsx5("span", { className: "h-full rounded-r-md border border-l-0 border-brand-gray/30 bg-gray-100 px-4 py-2.5 text-base text-brand-gray", children: rightAddon })
      ] }),
      error && /* @__PURE__ */ jsxs5("p", { className: "mt-2 text-sm text-brand-error flex items-center gap-1", children: [
        /* @__PURE__ */ jsx5(
          "svg",
          {
            className: "h-4 w-4",
            fill: "currentColor",
            viewBox: "0 0 20 20",
            xmlns: "http://www.w3.org/2000/svg",
            children: /* @__PURE__ */ jsx5(
              "path",
              {
                fillRule: "evenodd",
                clipRule: "evenodd",
                d: "M10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5ZM10 5.83333C10.4602 5.83333 10.8333 6.20643 10.8333 6.66667V10C10.8333 10.4602 10.4602 10.8333 10 10.8333C9.53976 10.8333 9.16667 10.4602 9.16667 10V6.66667C9.16667 6.20643 9.53976 5.83333 10 5.83333ZM10 12.5C9.53976 12.5 9.16667 12.8731 9.16667 13.3333C9.16667 13.7936 9.53976 14.1667 10 14.1667H10.0083C10.4686 14.1667 10.8417 13.7936 10.8417 13.3333C10.8417 12.8731 10.4686 12.5 10.0083 12.5H10Z"
              }
            )
          }
        ),
        error
      ] }),
      hint && !error && /* @__PURE__ */ jsx5("p", { className: "mt-2 text-sm text-brand-gray", children: hint })
    ] });
  }
);
Input.displayName = "Input";

// src/hooks/useModal.ts
import { useEffect, useRef, useState } from "react";
var useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);
  const triggerRef = useRef(null);
  const modalRef = useRef(null);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(!isOpen);
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!modalRef.current) return;
      if (!isOpen || modalRef.current.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      close();
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [isOpen]);
  useEffect(() => {
    const keyHandler = ({ key }) => {
      if (!isOpen || key !== "Escape") return;
      close();
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [isOpen]);
  return {
    isOpen,
    open,
    close,
    toggle,
    triggerRef,
    modalRef
  };
};

// src/components/SEO.tsx
import { useEffect as useEffect2 } from "react";
function SEO({
  title,
  description,
  canonical,
  keywords,
  ogImage = "https://theholetruth.org/og-default.jpg",
  twitterCard = "summary_large_image",
  schema,
  siteName = "The HOLE Foundation",
  type = "website",
  author,
  publishedTime,
  modifiedTime,
  robots = "index,follow",
  lang = "en"
}) {
  useEffect2(() => {
    document.title = title;
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };
    const updateLinkTag = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };
    updateMetaTag("description", description);
    if (keywords) {
      updateMetaTag("keywords", keywords);
    }
    updateMetaTag("robots", robots);
    document.documentElement.lang = lang;
    updateMetaTag("og:type", type, true);
    updateMetaTag("og:url", canonical, true);
    updateMetaTag("og:title", title, true);
    updateMetaTag("og:description", description, true);
    updateMetaTag("og:image", ogImage, true);
    updateMetaTag("og:site_name", siteName, true);
    if (type === "article") {
      if (author) updateMetaTag("article:author", author, true);
      if (publishedTime) updateMetaTag("article:published_time", publishedTime, true);
      if (modifiedTime) updateMetaTag("article:modified_time", modifiedTime, true);
    }
    updateMetaTag("twitter:card", twitterCard);
    updateMetaTag("twitter:url", canonical);
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", ogImage);
    updateLinkTag("canonical", canonical);
    if (schema) {
      let scriptElement = document.querySelector('script[type="application/ld+json"]');
      if (!scriptElement) {
        scriptElement = document.createElement("script");
        scriptElement.setAttribute("type", "application/ld+json");
        document.head.appendChild(scriptElement);
      }
      scriptElement.textContent = JSON.stringify(schema);
    }
  }, [
    title,
    description,
    canonical,
    keywords,
    ogImage,
    twitterCard,
    schema,
    siteName,
    type,
    author,
    publishedTime,
    modifiedTime,
    robots,
    lang
  ]);
  return null;
}
var SchemaGenerators = {
  /**
   * Generate Schema.org markup for a wiki page about state FOIA laws
   */
  wikiPage: (state, statuteName, citation, url) => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${state} FOIA Laws`,
    description: `Complete guide to ${statuteName} (${citation})`,
    url,
    mainEntity: {
      "@type": "GovernmentService",
      name: statuteName,
      serviceType: "Public Records Request",
      areaServed: {
        "@type": "State",
        name: state
      },
      provider: {
        "@type": "GovernmentOrganization",
        name: `State of ${state}`
      }
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://theholetruth.org"
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Wiki",
          item: "https://theholetruth.org/wiki"
        },
        {
          "@type": "ListItem",
          position: 3,
          name: state,
          item: url
        }
      ]
    }
  }),
  /**
   * Generate Schema.org markup for the Foundation organization
   */
  organization: () => ({
    "@context": "https://schema.org",
    "@type": "NGO",
    name: "The HOLE Foundation",
    alternateName: "Honest Oversight & Legal Ethics Foundation",
    url: "https://theholefoundation.org",
    logo: "https://theholefoundation.org/logo.png",
    description: "Nonprofit deploying AI for government transparency and accountability",
    foundingDate: "2024",
    founder: {
      "@type": "Person",
      name: "Dr. Joseph T. Herrmann"
    },
    sameAs: [
      "https://theholetruth.org",
      "https://github.com/The-HOLE-Foundation"
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      email: "contact@theholefoundation.org"
    }
  }),
  /**
   * Generate Schema.org markup for the FOIA Generator tool
   */
  webApplication: () => ({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "FOIA Request Generator",
    description: "AI-powered tool to generate Freedom of Information Act requests for any US jurisdiction",
    url: "https://theholetruth.org/foia-generator",
    applicationCategory: "GovernmentApplication",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD"
    },
    featureList: [
      "Generate FOIA requests for all 50 states + Federal",
      "AI-powered request drafting",
      "State-specific exemptions guidance",
      "Citation lookup"
    ]
  }),
  /**
   * Generate Schema.org markup for mission/about pages
   */
  aboutPage: (title, description, url) => ({
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: title,
    description,
    url,
    mainEntity: {
      "@type": "NGO",
      name: "The HOLE Foundation",
      url: "https://theholefoundation.org"
    }
  })
};
export {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  ContentCard,
  ContentCardGrid,
  FeatureCard,
  Input,
  SEO,
  SchemaGenerators,
  StatCard,
  cn,
  useModal
};
//# sourceMappingURL=index.js.map